<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { CheckIcon, PlusIcon, UserPlusIcon, UserIcon, XMarkIcon } from '@heroicons/vue/20/solid'
import CropperModal from '@/components/modals/CropperModal.vue'
import imageCompression from 'browser-image-compression'
import SpinnerLoader from '@/components/common/SpinnerLoader.vue'
import { useToast } from 'vue-toastification'
import { useDebounceFn } from '@vueuse/core'
import {
  type CompressionOptions,
  type IWidgetAssistantPhoto,
  type Project,
  type ProjectSettings,
} from '@/components/projects/interfaces.ts'
import { cloneDeep, isNull, isUndefined } from 'lodash'
import {
  FILE_PROCESS_DEBOUNCE_MS,
  getCompressionImageOptions,
  validateFileTypeAndSize,
} from '@/config/image.const.ts'
import { Field, ErrorMessage } from 'vee-validate'
import { useMutation } from '@vue/apollo-composable'
import {
  DeleteAssistantPhoto,
  UploadAssistantPhoto,
} from '@/components/projects/graphql/project.mutations.ts'
import { base64ToFile } from '@/utils/util.ts'
import { useUserStore } from '@/stores/user.store.ts'
import { storeToRefs } from 'pinia'
import { FindProjects_WidgetAssistantPhotos_PlatformLanguages } from '@/components/projects/graphql/project.queries.ts'
import { ApolloCache } from '@apollo/client'
import { WIDGET_ASSISTANT_AVATARS_MAX_AMOUNT } from '@/config/visual.const.ts'
import { API_BASE_URL, API_URL } from '@/config/env.const.ts'
import { useTranslations } from '@/composables/useTranslations.ts'
import { ProjectSettingFormKey } from '@/components/projects/settings.types.ts'
import { ProjectSettingsField } from '@/enums.ts'
import type { PlatformLanguage } from '@/components/interfaces/graphql-common.interfaces.ts'

const props = defineProps<{
  photos: IWidgetAssistantPhoto[]
}>()
const { t } = useTranslations()

const toast = useToast()
const userStore = useUserStore()
const { projectSelected } = storeToRefs(userStore)
const { defineField } = inject(ProjectSettingFormKey)
const [ widgetVisualAvatarUrl ] = defineField(ProjectSettingsField.WidgetVisualAvatarUrl)
const selectedAvatarIndex = ref<number | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)
const compressionProgress = ref(0)
const isCompressing = ref(false)
const abortController = ref<AbortController>()
const imageUrl = ref<string | null>(null)
const croppedImage = ref<string | null>(null)
const isModalOpen = ref<boolean>(false)
const croppedFileName = ref<File | null>(null)
const previouslyUsedAvatars = ref<Set<string>>(new Set())

function setCompressing(key: boolean): void {
  isCompressing.value = key
}

const {
  onDone: onUploadCroppedImageDone,
  onError: onUploadCroppedImageError,
  mutate: onUploadCroppedImageClicked,
} = useMutation<{ file: IWidgetAssistantPhoto }, { projectId: number; req: File }>(
  UploadAssistantPhoto,
  { fetchPolicy: 'no-cache' },
)

onUploadCroppedImageDone((response) => {
  console.log('onUploadCroppedImageDone | onDone', response.data)
  toast.success('The cropped image was uploaded!')
})

onUploadCroppedImageError((err) => {
  console.log('onUploadCroppedImageError | onError:', err)
})

const {
  onDone: onDeleteAssistantImageDone,
  onError: onDeleteAssistantImageError,
  mutate: onDeleteAssistantImageClicked,
} = useMutation<
  { deleteWidgetAssistantPhoto: { succeed: boolean } },
  { projectId: number; fileKey: string | null }
>(DeleteAssistantPhoto, { fetchPolicy: 'no-cache' })

onDeleteAssistantImageDone((response) => {
  console.log('onDeleteAssistantImageDone | onDone', response.data)
  toast.success('The cropped image was deleted!')
})

onDeleteAssistantImageError((err) => {
  console.log('onDeleteAssistantImageError | onError:', err)
})

function readProjectCache(
  cache: ApolloCache<ProjectSettings>,
  projectId: number,
): {
  projects: ProjectSettings
  languages: PlatformLanguage[]
  widgetAssistantPhotos: IWidgetAssistantPhoto[]
} | null {
  return cache.readQuery<{
    projects: ProjectSettings
    languages: PlatformLanguage[]
    widgetAssistantPhotos: IWidgetAssistantPhoto[]
  }>({
    query: FindProjects_WidgetAssistantPhotos_PlatformLanguages,
    variables: {
      input: { projectIds: [projectId] },
      projectId: projectId,
    },
  })
}

const displayItems = computed<IWidgetAssistantPhoto[]>(() => {
  const items: IWidgetAssistantPhoto[] = []

  // Add upload button
  if (props.photos.length < WIDGET_ASSISTANT_AVATARS_MAX_AMOUNT) {
    items.push({
      filePath: 'upload',
      isDefault: true,
      fileKey: '',
      fileName: '',
    })
  }

  const uniqueFilePaths = new Set<string>()

  props.photos.forEach((photo) => {
    if (photo.filePath) {
      uniqueFilePaths.add(photo.filePath)
    }
  })

  previouslyUsedAvatars.value.forEach((path) => {
    if (path) {
      uniqueFilePaths.add(path)
    }
  })

  uniqueFilePaths.forEach((filePath) => {
    const fromPhotos = props.photos.find((photo) => photo.filePath === filePath)
    items.push(
      fromPhotos ?? {
        filePath,
        isDefault: true,
        fileKey: null,
        fileName: 'persisted-avatar',
      },
    )
  })

  const currentCount = items.length
  if (currentCount < WIDGET_ASSISTANT_AVATARS_MAX_AMOUNT) {
    const emptySlotsNeeded = WIDGET_ASSISTANT_AVATARS_MAX_AMOUNT - currentCount
    for (let i = 0; i < emptySlotsNeeded; i++) {
      items.push({
        filePath: '',
        isDefault: false,
        fileKey: null,
        fileName: 'empty-slot',
      })
    }
  }

  return items
})

function onSelectAvatarItem(index: number): void {
  console.log('onSelectAvatarItem:', index, displayItems.value)
  const item = displayItems.value[index]

  console.log('item', item)

  if (isNull(item) || isUndefined(item) || item.filePath === 'upload') {
    return
  }

  selectedAvatarIndex.value = index
  const newValue = item.filePath || item.fileName
  if (!isNull(newValue)) {
    widgetVisualAvatarUrl.value = newValue
  }
}

function onTriggerFileInput(): void {
  fileInput.value?.click()
}

const processFileDebounced = useDebounceFn(async (file: File) => {
  try {
    abortController.value?.abort()
    abortController.value = new AbortController()

    validateFileTypeAndSize(file)
    setCompressing(true)
    croppedFileName.value = file

    const options: CompressionOptions = {
      ...getCompressionImageOptions(),
      signal: abortController.value.signal,
      onProgress: (progress: number) => {
        compressionProgress.value = progress
      },
    }

    const compressedFile = await imageCompression(file, options)
    imageUrl.value = await imageCompression.getDataUrlFromFile(compressedFile)
    croppedImage.value = null
    isModalOpen.value = true
  } catch (error: unknown) {
    if (error instanceof Error && error.name !== 'AbortError') {
      toast.error(error.message)
    }
  } finally {
    setCompressing(false)
    compressionProgress.value = 0
  }
}, FILE_PROCESS_DEBOUNCE_MS)

async function onFileSelect(event: Event) {
  const input = event.target as HTMLInputElement
  const files = input.files

  if (!files) return

  try {
    for (const file of Array.from(files)) {
      await processFileDebounced(file)
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      toast.error('Error processing files: ' + error.message)
    }
  } finally {
    input.value = ''
  }
}

function onSaveCroppedImage(croppedDataUrl: string): void {
  croppedImage.value = croppedDataUrl
  const projectId = (projectSelected.value as Project).id as number

  onUploadCroppedImageClicked(
    {
      projectId: projectId,
      req: base64ToFile(croppedImage.value, croppedFileName?.value?.name ?? ''),
    },
    {
      fetchPolicy: 'no-cache',
      update: (cache, result) => {
        const file = result.data?.file
        if (isNull(file) || isUndefined(file)) {
          return
        }

        const { projects, languages, widgetAssistantPhotos } = readProjectCache(cache, projectId) || {}
        if (!Array.isArray(widgetAssistantPhotos)) {
          return
        }

        console.log('onUploadCroppedImageClicked ++> ', widgetAssistantPhotos)

        cache.writeQuery({
          query: FindProjects_WidgetAssistantPhotos_PlatformLanguages,
          data: {
            widgetAssistantPhotos: cloneDeep(widgetAssistantPhotos).concat(file),
            projects,
            languages,
          },
          variables: {
            input: { projectIds: [projectId] },
            projectId: projectId,
          },
        })
      },
    },
  )

  isModalOpen.value = false
}

function onDeleteAvatar(index: number): void {
  const item = displayItems.value[index]

  if (isNull(item) || isUndefined(item)) {
    return
  }
  const projectId = (projectSelected.value as Project).id as number

  onDeleteAssistantImageClicked(
    {
      projectId: projectId,
      fileKey: item.fileKey,
    },
    {
      fetchPolicy: 'no-cache',
      update: (cache, result) => {
        const { deleteWidgetAssistantPhoto } = result?.data || {}
        if (!deleteWidgetAssistantPhoto?.succeed) {
          return
        }

        const { projects, languages, widgetAssistantPhotos } = readProjectCache(cache, projectId) || {}

        if (!Array.isArray(widgetAssistantPhotos)) {
          return
        }

        cache.writeQuery({
          query: FindProjects_WidgetAssistantPhotos_PlatformLanguages,
          data: {
            projects,
            languages,
            widgetAssistantPhotos: cloneDeep(widgetAssistantPhotos).filter((photo) => photo.fileKey !== item.fileKey),
          },
          variables: {
            input: { projectIds: [projectId] },
            projectId: projectId,
          },
        })
      },
    },
  )

  if (!item?.isDefault) {
    if (selectedAvatarIndex.value === index) {
      selectedAvatarIndex.value = null
      widgetVisualAvatarUrl.value = ''
    }

    if (
      !isNull(selectedAvatarIndex.value) &&
      !isUndefined(selectedAvatarIndex.value) &&
      selectedAvatarIndex.value <= index
    ) {
      // selectedAvatarIndex.value += 1
      widgetVisualAvatarUrl.value = displayItems.value[selectedAvatarIndex.value!]?.filePath ?? ''
    }
  }
}

function getFullFilePath(filePath: string): string {
  return filePath.startsWith('data:image') ? filePath : `${API_BASE_URL}${filePath}`
}

function findSelectedAvatarIndex(): number {
  return displayItems.value.findIndex((item) => item.filePath === widgetVisualAvatarUrl.value)
}

onMounted(() => {
  selectedAvatarIndex.value = findSelectedAvatarIndex()
})

watch([() => props.photos, displayItems], () => {
  selectedAvatarIndex.value = findSelectedAvatarIndex()
})

onUnmounted(() => {
  abortController.value?.abort()
})

watch(() => widgetVisualAvatarUrl.value, (newVal) => {
    selectedAvatarIndex.value = findSelectedAvatarIndex()
    if (newVal) {
      previouslyUsedAvatars.value.add(newVal)
    }
  },
  { immediate: true },
)
</script>

<template>
  <div>
    <Field :name="ProjectSettingsField.WidgetVisualAvatarUrl" v-model="widgetVisualAvatarUrl" v-slot="{ field }">
      <input type="hidden" v-bind="field" />
      <input
        ref="fileInput"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        class="hidden"
        @input="onFileSelect"
      />
      <div class="flex items-center p-2 flex-wrap gap-2 max-w-full relative">
        <div
          v-for="(item, index) in displayItems"
          :key="index + '_' + (item.filePath || 'empty')"
          class="size-12 md:size-16 rounded-full flex items-center justify-center relative border-3 transition duration-300 ease-in-out transform"
          :class="{
            'scale-100 border-gray-100 cursor-default hover:scale-100 shadow-none': !item.filePath,
            'border-fuchsia scale-105': selectedAvatarIndex === index,
            'border-gray-300 hover:scale-105 cursor-pointer':
              item.filePath !== '' && selectedAvatarIndex !== index && item.filePath !== 'upload',
            'shadow-sm cursor-default': selectedAvatarIndex !== index,
          }"
        >
          <div
            class="absolute z-20 w-[125%] h-[125%] flex justify-center items-center bg-white"
            v-if="index === 0 && isCompressing"
          >
            <SpinnerLoader type="purpleCustom" :size="40" />
          </div>
          <button
            v-if="item.filePath === 'upload'"
            type="button"
            class="relative w-full h-full rounded-full bg-white flex items-center justify-center cursor-pointer transition-all"
            @click="onTriggerFileInput"
          >
            <UserPlusIcon class="w-6 h-6 text-gray-300" aria-hidden="true" />
            <div
              class="absolute top-[-6px] right-[-6px] border-2 border-gray-300 w-6 h-6 flex items-center justify-center rounded-full p-1 bg-white"
            >
              <PlusIcon class="w-6 h-6 text-gray-300" aria-hidden="true" />
            </div>
          </button>
          <div
            class="w-full h-full"
            v-else-if="item.filePath && item.filePath !== 'upload'"
            @click="onSelectAvatarItem(index)"
          >
            <img
              :src="getFullFilePath(item.filePath)"
              alt="avatarUrl"
              class="w-full h-full object-cover rounded-full"
            />
            <div
              v-if="selectedAvatarIndex === index"
              class="absolute z-10 top-[-6px] right-[-6px] border-2 border-fuchsia w-6 h-6 flex items-center justify-center rounded-full p-1 bg-white shadow-md transition-transform duration-300"
            >
              <CheckIcon class="w-6 h-6 stroke-2 text-fuchsia" aria-hidden="true" />
            </div>
            <div
              v-if="item.filePath !== 'upload' && selectedAvatarIndex !== index && !item.isDefault"
              class="absolute top-[-6px] right-[-6px] border-2 border-purpleCustom w-6 h-6 flex items-center justify-center rounded-full p-1 bg-white shadow-md cursor-pointer hover:bg-red-50"
              @click.stop="onDeleteAvatar(index)"
            >
              <XMarkIcon class="w-6 h-6 text-purpleCustom" aria-hidden="true" />
            </div>
          </div>
          <div v-else class="relative flex items-center justify-center h-full">
            <UserIcon class="w-6 h-6 text-gray-100" aria-hidden="true" />
          </div>
        </div>
      </div>
    </Field>
    <ErrorMessage
      :name="ProjectSettingsField.WidgetVisualAvatarUrl"
      class="text-sm font-500 text-red-400 absolute top-full text-center w-full"
    />
    <!--    <div class="compression-indicator" v-if="isCompressing">-->
    <!--      <div class="progress-bar">-->
    <!--        <div class="progress-fill" :style="{ width: `${compressionProgress}%` }"></div>-->
    <!--      </div>-->
    <!--      <span class="progress-text"> Compressing: {{ Math.round(compressionProgress) }}% </span>-->
    <!--    </div>-->
    <CropperModal
      :open="isModalOpen"
      :image-url="imageUrl"
      @close="isModalOpen = false"
      @save="onSaveCroppedImage"
      :compressing="isCompressing"
    >
      <div v-if="isCompressing" class="modal-loader">
        <slot name="loader">
          <SpinnerLoader />
          <p>{{ t('settings.visual.optimizingImage') }}</p>
        </slot>
      </div>
    </CropperModal>
  </div>
</template>

<style scoped>
.compression-indicator {
  @apply w-full mt-2 flex items-center gap-2;
}

.progress-bar {
  @apply h-2 bg-gray-200 rounded-full flex-1;
}

.progress-fill {
  @apply h-full bg-blue-500 rounded-full transition-all duration-300;
}

.progress-text {
  @apply text-sm text-gray-600 min-w-[70px];
}
</style>
